import { gql, useMutation, useQuery } from '@apollo/client'
import cx from 'classnames'
import { without } from 'lodash'
import { useState } from 'react'
import {
  DeleteFailedTdlResultsMutation,
  DeleteFailedTdlResultsMutationVariables,
} from './__generated__/DeleteFailedTdlResultsMutation'
import { FailedTdlResultsQuery } from './__generated__/FailedTdlResultsQuery'

const failedTdlResultsQuery = gql`
  query FailedTdlResultsQuery {
    failedTdlResults {
      id
      filename
      content
      error
    }
  }
`

const deleteFailedTdlResultsMutation = gql`
  mutation DeleteFailedTdlResultsMutation($ids: [ID!]!) {
    deleteFailedTdlResults(ids: $ids) {
      deletedIds
      failedIds {
        id
        reason
      }
    }
  }
`

const FailedTdlResults = () => {
  const { loading, error, data } = useQuery<FailedTdlResultsQuery>(failedTdlResultsQuery)

  const [deleteFailedTdlResults, deleteFailedTdlResultsResponse] = useMutation<
    DeleteFailedTdlResultsMutation,
    DeleteFailedTdlResultsMutationVariables
  >(deleteFailedTdlResultsMutation)

  const [selectedIds, setSelectedIds] = useState<string[]>([])

  if (error) {
    throw error
  }

  if (loading) {
    return <p>Loading...</p>
  }

  const onDelete = async () => {
    await deleteFailedTdlResults({
      variables: { ids: selectedIds },
      refetchQueries: ['FailedTdlResultsQuery'],
      awaitRefetchQueries: true,
    })

    setSelectedIds([])
  }

  return (
    <>
      {selectedIds.length > 0 && (
        <button
          className={cx('button is-danger mb-3', {
            'is-loading': deleteFailedTdlResultsResponse.loading,
          })}
          onClick={onDelete}
        >
          Delete {selectedIds.length} results
        </button>
      )}

      {deleteFailedTdlResultsResponse.data?.deleteFailedTdlResults.deletedIds.length > 0 && (
        <article className="message is-success">
          <div className="message-body">
            Deleted {deleteFailedTdlResultsResponse.data?.deleteFailedTdlResults.deletedIds.length} results
          </div>
        </article>
      )}

      {deleteFailedTdlResultsResponse.data?.deleteFailedTdlResults.failedIds.length > 0 && (
        <article className="message is-warning">
          <div className="message-body">
            Failed to delete some results:
            <ul>
              {deleteFailedTdlResultsResponse.data.deleteFailedTdlResults.failedIds.map((failed) => (
                <li key={failed.id}>
                  {failed.id}: {failed.reason}
                </li>
              ))}
            </ul>
          </div>
        </article>
      )}

      <table className="table is-bordered is-fullwidth">
        <thead>
          <tr>
            <th></th>
            <th>Filename</th>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          {data.failedTdlResults.map((result) => (
            <tr key={result.id}>
              <td>
                <input
                  type="checkbox"
                  onChange={(e) => {
                    const checked = e.target.checked
                    setSelectedIds((prev) => {
                      if (checked) {
                        return [...prev, result.id]
                      }

                      return without(prev, result.id)
                    })
                  }}
                />
              </td>
              <td>{result.filename}</td>
              <td>{result.error}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

export default FailedTdlResults
